import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { IconButton } from '@americanexpress/dls-react';
import { IconChevronLeft, IconChevronRight } from '@americanexpress/dls-icons';
import styles from './LFCarousel.module.scss';

const getCardsPerView = () => {
  if (window.matchMedia('(min-width: 1280px)').matches) return 4;
  if (window.matchMedia('(min-width: 1024px)').matches) return 3;
  if (window.matchMedia('(min-width:568px)').matches) return 2;

  return 1;
};

const LFCarousel = ({
  children,
  className,
  innerContainerClassName,
  ...rest
}: {
  children: React.ReactNode[];
  className?: string;
  innerContainerClassName?: string;
}) => {
  const [cardsPerView, setCardsPerView] = useState(1);
  const [page, setPage] = useState(0);

  const cards = React.Children.toArray(children);

  const pages = useMemo(() => {
    const slides: React.ReactNode[][] = [];

    for (let i = 0; i < cards.length; i += cardsPerView) {
      const group = cards.slice(i, i + cardsPerView);

      slides.push(group);
    }
    return slides;
  }, [cards, cardsPerView]);

  const totalPages = pages.length;

  useEffect(() => {
    const updateCardsPerView = () => {
      const nextCardsPerView = getCardsPerView();

      setCardsPerView((previousCardsPerView) => {
        setPage((currentPage) => {
          const firstVisibleCardIdx = currentPage * previousCardsPerView;
          const nextPage = Math.floor(firstVisibleCardIdx / nextCardsPerView);
          return Math.min(
            nextPage,
            Math.ceil(cards.length / nextCardsPerView) - 1
          );
        });

        return nextCardsPerView;
      });
    };

    updateCardsPerView();
    window.addEventListener('resize', updateCardsPerView);

    return () => {
      window.removeEventListener('resize', updateCardsPerView);
    };
  }, [cards.length]);

  const goToNext = () => {
    setPage((current) => {
      if (current === totalPages - 1) return 0;
      return current + 1;
    });
  };

  const goToPrevious = () => {
    setPage((current) => {
      if (current === 0) return totalPages - 1;
      return current - 1;
    });
  };

  return (
    <div className={`${styles.carousel} ${className || ''}`} {...rest}>
      <div
        className={`${styles.carouselContainer} ${innerContainerClassName || ''}`}
      >
        <div
          className={clsx(styles.carouselContent)}
          style={{
            transform: `translateX(-${page * 100}%)`,
          }}
        >
          {/* Carousel Page */}
          {pages.map((group) => (
            <div
              className={styles.carouselPage}
              key={(group[0] as React.ReactElement).key}
            >
              {/* Carousel Item */}
              {group.map((child) => (
                <div
                  className={styles.carouselItem}
                  key={(child as React.ReactElement).key}
                >
                  {child}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* pagination */}
      {totalPages > 1 && (
        <div className={styles.carouselControls}>
          <IconButton
            screenReaderLabel="Previous page"
            variant="tertiary"
            aria-label="Previous page"
            onClick={goToPrevious}
          >
            <IconChevronLeft />
          </IconButton>
          <div
            className={styles.carouselIndicators}
            aria-label="Carousel indicators"
          >
            {pages.map((group, index) => (
              <span
                key={(group[0] as React.ReactElement).key}
                className={clsx(styles.carouselDot, {
                  [styles.carouselDotActive]: index === page,
                })}
                aria-label={`Go to page ${index + 1}`}
                aria-current={index === page}
              />
            ))}
          </div>

          <IconButton
            screenReaderLabel="Next page"
            variant="tertiary"
            aria-label="Next page"
            onClick={goToNext}
          >
            <IconChevronRight />
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default LFCarousel;
